/**
 * Konnect Onboarding Wizard — Sprint 2 (Subscriber Onboarding Intelligence Engine)
 * Reference: /app/memory/AUTOMATION_ENGINE_MASTER.md § PHASE 0
 *
 * 4-step guided setup:
 *   Step 1 — Company Profile (industry, size, region, currency…)
 *   Step 2 — Org Structure (auto-suggested departments + roles, editable)
 *   Step 3 — Module Activation (AI-recommended set based on profile, editable)
 *   Step 4 — Review + Complete (seeds starter automations from Sprint 1)
 *
 * All state persisted per-tenant on backend so the wizard is resumable.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Building2, Users, Zap, CheckCircle2, ArrowRight, ArrowLeft, Sparkles, Rocket } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/onboarding`;

const authHeaders = () => {
  const token = localStorage.getItem('konnect_access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function OnboardingWizardPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [ref, setRef] = useState({ industries: [], size_buckets: [], business_types: [] });
  const [status, setStatus] = useState(null);
  const [profile, setProfile] = useState({
    company_name: '', industry: '', country: '', currency: 'USD',
    timezone: 'UTC', language: 'en', size_bucket: '', business_type: 'B2B',
    primary_function: '', current_tools: [],
  });
  const [org, setOrg] = useState([]);
  const [modules, setModules] = useState([]);
  const [recos, setRecos] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reference-data`).then((r) => setRef(r.data));
    axios.get(`${API}/status`, { headers: authHeaders() }).then((r) => {
      setStatus(r.data);
      if (r.data.profile) setProfile(r.data.profile);
      if (r.data.org_structure) setOrg(r.data.org_structure);
      if (r.data.modules_activated?.length) setModules(r.data.modules_activated);
      if (r.data.completed) setStep(4);
    }).catch(() => {});
  }, []);

  const progress = ((step - 1) / 3) * 100;

  const saveProfile = useCallback(async () => {
    if (!profile.company_name || !profile.industry || !profile.size_bucket) {
      toast.error('Company name, industry, and size are required');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/profile`, profile, { headers: authHeaders() });
      // fetch recommendations immediately
      const r = await axios.get(`${API}/recommendations`, { headers: authHeaders() });
      setRecos(r.data);
      setOrg(r.data.recommended_org_structure || []);
      setModules(r.data.recommended_modules || []);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setLoading(false); }
  }, [profile]);

  const saveOrg = useCallback(async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/org-structure`, { departments: org }, { headers: authHeaders() });
      setStep(3);
    } catch { toast.error('Save org structure failed'); }
    finally { setLoading(false); }
  }, [org]);

  const saveModules = useCallback(async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/activate-modules`, { modules }, { headers: authHeaders() });
      setStep(4);
    } catch { toast.error('Activate modules failed'); }
    finally { setLoading(false); }
  }, [modules]);

  const complete = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/complete`, {}, { headers: authHeaders() });
      toast.success(`Setup complete — ${r.data.automations_seeded} automations activated`);
      setTimeout(() => navigate('/automation-center'), 1500);
    } catch { toast.error('Complete failed'); }
    finally { setLoading(false); }
  }, [navigate]);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="onboarding-wizard">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Rocket className="h-6 w-6 text-emerald-600" />
          <h1 className="text-2xl sm:text-3xl font-heading font-bold" data-testid="wizard-title">Welcome to Konnect</h1>
          <Badge className="bg-emerald-100 text-emerald-800 border-0">Setup Wizard</Badge>
        </div>
        <p className="text-sm text-gray-medium">4 quick steps and we'll auto-configure your business operations.</p>
        <div className="mt-4">
          <Progress value={progress} className="h-2" data-testid="wizard-progress" />
          <div className="flex justify-between text-xs text-gray-medium mt-1">
            <span>Step {step} of 4</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
        </div>
      </div>

      {/* Step 1 — Profile */}
      {step === 1 && (
        <Card data-testid="step-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><Building2 className="h-5 w-5 text-emerald-600" /> Company Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Company name *"><Input value={profile.company_name} onChange={(v) => setProfile({...profile, company_name: v})} testid="company-name" /></Field>
              <Field label="Country *"><Input value={profile.country} onChange={(v) => setProfile({...profile, country: v})} testid="country" placeholder="e.g. UAE, Philippines" /></Field>
              <Field label="Industry *">
                <Select value={profile.industry} onChange={(v) => setProfile({...profile, industry: v})} testid="industry" options={ref.industries.map(i => ({v: i.key, l: i.label}))} />
              </Field>
              <Field label="Company size *">
                <Select value={profile.size_bucket} onChange={(v) => setProfile({...profile, size_bucket: v})} testid="size" options={ref.size_buckets.map(s => ({v: s.key, l: s.label}))} />
              </Field>
              <Field label="Business type">
                <Select value={profile.business_type} onChange={(v) => setProfile({...profile, business_type: v})} testid="btype" options={ref.business_types.map(b => ({v: b, l: b}))} />
              </Field>
              <Field label="Currency"><Input value={profile.currency} onChange={(v) => setProfile({...profile, currency: v})} testid="currency" /></Field>
            </div>
            <div className="flex justify-end pt-3">
              <Button onClick={saveProfile} disabled={loading} className="bg-emerald-brand hover:bg-emerald-hover" data-testid="step-1-next">
                {loading ? 'Analyzing…' : 'Get AI Recommendations'}<ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2 — Org */}
      {step === 2 && (
        <Card data-testid="step-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><Users className="h-5 w-5 text-emerald-600" /> Organization Structure</CardTitle>
            {recos && <p className="text-sm text-gray-medium">AI-suggested for a <b>{recos.tier}</b>-tier <b>{recos.industry}</b> company. Edit as needed.</p>}
          </CardHeader>
          <CardContent className="space-y-3">
            {org.map((d, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <input value={d.name} onChange={(e) => { const c=[...org]; c[i].name = e.target.value; setOrg(c); }}
                    className="font-semibold text-emerald-brand bg-transparent border-b border-transparent hover:border-emerald-200 focus:border-emerald-600 focus:outline-none" />
                  <button onClick={() => setOrg(org.filter((_, x) => x !== i))} className="text-xs text-red-500 hover:underline">Remove</button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {d.roles.map((r, j) => (
                    <Badge key={j} variant="outline" className="text-xs bg-emerald-50 border-emerald-200 text-emerald-800">{r}</Badge>
                  ))}
                </div>
              </div>
            ))}
            <button onClick={() => setOrg([...org, { name: 'New Department', roles: [] }])}
              className="text-sm text-emerald-brand hover:underline">+ Add department</button>
            <div className="flex justify-between pt-3">
              <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
              <Button onClick={saveOrg} disabled={loading} className="bg-emerald-brand hover:bg-emerald-hover" data-testid="step-2-next">Continue <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — Modules */}
      {step === 3 && (
        <Card data-testid="step-3">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><Zap className="h-5 w-5 text-emerald-600" /> Recommended Modules</CardTitle>
            {recos && <p className="text-sm text-gray-medium">
              We recommend <b>{recos.recommended_modules.length} modules</b> for you.
              Estimated savings: <b className="text-emerald-brand">~{recos.estimated_savings_hours_month} hours/month</b>.
            </p>}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {(recos?.recommended_modules || modules).map((m) => (
                <label key={m} className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition ${modules.includes(m) ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 hover:border-emerald-200'}`}>
                  <input type="checkbox" checked={modules.includes(m)} onChange={(e) => {
                    setModules(e.target.checked ? [...modules, m] : modules.filter(x => x !== m));
                  }} className="rounded text-emerald-600" data-testid={`module-${m}`} />
                  <span className="text-sm capitalize">{m.replace(/_/g, ' ')}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
              <Button onClick={saveModules} disabled={loading || !modules.length} className="bg-emerald-brand hover:bg-emerald-hover" data-testid="step-3-next">Activate {modules.length} Modules <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4 — Complete */}
      {step === 4 && (
        <Card data-testid="step-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><Sparkles className="h-5 w-5 text-emerald-600" /> Ready to Launch</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-6">
              <ReviewRow label="Company" value={profile.company_name || '—'} />
              <ReviewRow label="Industry / Tier" value={`${profile.industry} / ${recos?.tier || '—'}`} />
              <ReviewRow label="Departments" value={`${org.length} configured`} />
              <ReviewRow label="Modules" value={`${modules.length} activated`} />
              <ReviewRow label="First automations" value={`${recos?.recommended_workflows?.length || 3} will be seeded`} />
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900">
                  <b>What happens when you click Launch:</b>
                  <ul className="list-disc pl-5 mt-1 text-emerald-800 space-y-0.5 text-xs">
                    <li>Your recommended modules become active in your Konnect workspace</li>
                    <li>3+ starter automations are seeded and ready to run</li>
                    <li>Your dashboard is pre-configured with industry-standard KPIs</li>
                    <li>You can further customize anything from Settings anytime</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
              <Button onClick={complete} disabled={loading} className="bg-emerald-brand hover:bg-emerald-hover px-8" size="lg" data-testid="wizard-launch">
                <Rocket className="mr-2 h-4 w-4" /> Launch Konnect
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="text-xs text-gray-400 text-center pt-4">
        Sprint 2 · Onboarding Cascade · Auto-configures 89-module SaaS in under 3 minutes
      </div>
    </div>
  );
}

// ── mini helpers ──────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div><label className="text-xs font-semibold text-gray-medium mb-1 block">{label}</label>{children}</div>
);

const Input = ({ value, onChange, testid, placeholder }) => (
  <input value={value} onChange={(e) => onChange(e.target.value)}
    className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-brand"
    data-testid={testid} placeholder={placeholder} />
);

const Select = ({ value, onChange, testid, options }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)}
    className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-brand bg-white"
    data-testid={testid}>
    <option value="">Select…</option>
    {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
  </select>
);

const ReviewRow = ({ label, value }) => (
  <div className="flex justify-between text-sm border-b border-dashed border-gray-200 pb-1.5">
    <span className="text-gray-medium">{label}</span>
    <span className="font-semibold text-text-primary">{value}</span>
  </div>
);
